import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { blogService, getCategoryDisplayName } from '../../services/blogService';
import ArticleCard from './components/ArticleCard';
import FeaturedArticle from './components/FeaturedArticle';
import CategoryFilter from './components/CategoryFilter';
import SearchBar from './components/SearchBar';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryCounts, setCategoryCounts] = useState({});

  // Load featured and regular articles
  useEffect(() => {
    loadArticles();
    loadCategoryCounts();
  }, []);

  // Filter articles when category or search changes
  useEffect(() => {
    filterArticles();
  }, [selectedCategory, searchTerm]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      setError('');

      // Load featured articles
      const featured = await blogService?.getFeatured(2);
      setFeaturedArticles(featured);

      // Load all articles
      const all = await blogService?.getAll();
      setArticles(all);
    } catch (err) {
      console.error('Error loading articles:', err);
      setError('Failed to load blog articles. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryCounts = async () => {
    try {
      const counts = await blogService?.getCategoryCounts();
      setCategoryCounts(counts);
    } catch (err) {
      console.error('Error loading category counts:', err);
    }
  };

  const filterArticles = async () => {
    try {
      setLoading(true);

      let filtered = [];

      // Search takes priority
      if (searchTerm?.trim()) {
        filtered = await blogService?.search(searchTerm);
      } else if (selectedCategory !== 'all') {
        filtered = await blogService?.getByCategory(selectedCategory);
      } else {
        filtered = await blogService?.getAll();
      }

      setArticles(filtered);
    } catch (err) {
      console.error('Error filtering articles:', err);
      setError('Failed to filter articles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSearchTerm(''); // Clear search when changing category
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setSelectedCategory('all'); // Reset category when searching
  };

  return (
    <>
      <Helmet>
        <title>Blog - Bail Bonds Education & Resources | Sailors Bail Bonds</title>
        <meta 
          name="description" 
          content="Educational resources about bail process, legal rights, and family support from Sailors Bail Bonds in Sacramento. Helping families navigate challenging times." 
        />
        <meta 
          name="keywords" 
          content="bail bonds blog, legal rights, family support, Sacramento news, bail process guide" 
        />
      </Helmet>
      
      <div className="min-h-screen bg-white">
        <Header />
        
        <div className="min-h-screen bg-gray-50">
          {/* Hero Section */}
          <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  Educational Resources & Blog
                </h1>
                <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                  Helpful information about the bail process, legal rights, and support for families facing 
                  challenging times. We are here to guide you with knowledge and compassion.
                </p>
              </div>

              {/* Search Bar */}
              <div className="mt-8 max-w-2xl mx-auto">
                <SearchBar onSearch={handleSearch} initialValue={searchTerm} />
              </div>
            </div>
          </section>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Category Filter */}
            <CategoryFilter
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              categoryCounts={categoryCounts}
            />

            {/* Error Message */}
            {error && (
              <div 
                className="bg-red-50 border-l-4 border-red-500 p-4 mb-8" 
                role="alert"
                aria-live="polite"
              >
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12" role="status" aria-live="polite">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">Loading articles...</p>
              </div>
            )}

            {/* Featured Articles */}
            {!loading && !searchTerm && featuredArticles?.length > 0 && (
              <section className="mb-12" aria-labelledby="featured-heading">
                <h2 id="featured-heading" className="text-3xl font-bold text-gray-900 mb-6">
                  Featured Articles
                </h2>
                <div className="grid gap-8 md:grid-cols-2">
                  {featuredArticles?.map(article => (
                    <FeaturedArticle key={article?.id} article={article} />
                  ))}
                </div>
              </section>
            )}

            {/* Recent Articles */}
            {!loading && articles?.length > 0 && (
              <section aria-labelledby="articles-heading">
                <h2 id="articles-heading" className="text-3xl font-bold text-gray-900 mb-6">
                  {searchTerm ? 'Search Results' : selectedCategory !== 'all' ? getCategoryDisplayName(selectedCategory) : 'Recent Articles'}
                </h2>
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {// Exclude featured from regular list unless searching
                  articles?.filter(article => !article?.isFeatured || searchTerm)?.map(article => (
                      <ArticleCard key={article?.id} article={article} />
                    ))}
                </div>
              </section>
            )}

            {/* No Results */}
            {!loading && articles?.length === 0 && (
              <div className="text-center py-12">
                <svg 
                  className="mx-auto h-12 w-12 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No articles found</h3>
                <p className="mt-2 text-gray-500">
                  {searchTerm 
                    ? 'Try adjusting your search terms or browse by category.' :'Check back soon for new educational content.'}
                </p>
              </div>
            )}
          </div>

          {/* Contact CTA */}
          <section className="bg-blue-600 text-white py-12 mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl font-bold mb-4">Need Help Right Now?</h2>
              <p className="text-xl text-blue-100 mb-6">
                We are available 24/7 to answer your questions and provide support
              </p>
              <a 
                href="tel:9162563468" 
                className="inline-flex items-center px-8 py-3 border-2 border-white text-lg font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white transition-colors"
              >
                <svg 
                  className="mr-2 h-6 w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" 
                  />
                </svg>
                Call (916) 256-3468
              </a>
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default Blog;